/**
 * Org Memory Service — reads/writes per-org memory.md via the backend storage API.
 * Memory is stored in Supabase Storage bucket `org-memory/{orgId}/memory.md`.
 */

import type { OrganizationProfileData, QAStreamEvent } from '../types/boostPolicy';
import { supabase } from '@foundation/utils/supabaseClient';
import { getActiveWorkspaceId } from '../../../header/activeWorkspace';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const getAuthHeaders = async (orgId?: string) => {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  const userId = session?.user?.id;
  const resolvedOrgId = orgId ?? getActiveWorkspaceId() ?? '';

  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    'x-user-id': userId || '',
    'x-org-id': resolvedOrgId,
  };
};

/**
 * Fetch the org's memory.md content. Returns empty string if no memory exists yet.
 */
export const getOrgMemory = async (orgId?: string): Promise<string> => {
  const headers = await getAuthHeaders(orgId);
  const response = await fetch(`${API_BASE_URL}/api/revisr/org-memory`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || 'Failed to load memory');
  }

  const data = await response.json() as { content: string };
  return data.content ?? '';
};

/**
 * Ensures the section + category for these policy tags exist in memory.md.
 * Called on mount of the AI context step — creates the category even if no Q&A answers are given.
 * No-op if the category already exists.
 */
export const ensureCategoryExists = async (
  policyTags: string[],
  orgId?: string,
  documentName?: string,
): Promise<void> => {
  if (!policyTags.length) return;
  const headers = await getAuthHeaders(orgId);
  const response = await fetch(`${API_BASE_URL}/api/revisr/org-memory/ensure-category`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ policyTags, documentName }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || 'Failed to ensure category');
  }
};

/**
 * Append a Q&A pair to the org's memory.md, grouped by policy tag section and tag-derived category.
 */
export const appendToOrgMemory = async (
  question: string,
  answer: string,
  orgId?: string,
  policyTags?: string[],
  documentName?: string,
): Promise<void> => {
  const headers = await getAuthHeaders(orgId);
  const response = await fetch(`${API_BASE_URL}/api/revisr/org-memory/append`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ question, answer, policyTags: policyTags ?? [], documentName }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || 'Failed to save memory entry');
  }
};

/**
 * Use AI to extract 3–6 context items from memory.md relevant to a specific policy.
 * Returns an empty array if memory is blank or the request fails.
 */
export const extractRelevantContext = async (
  memoryContent: string,
  documentTitle: string,
  documentTags: string[],
  orgContext: OrganizationProfileData | null,
  orgId?: string,
): Promise<string[]> => {
  const headers = await getAuthHeaders(orgId);
  const response = await fetch(`${API_BASE_URL}/api/revisr/org-memory/extract-relevant`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ memoryContent, documentTitle, documentTags, orgContext }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || 'Failed to extract context');
  }

  const data = await response.json() as { items: string[] };
  return data.items ?? [];
};

export type QATurnPayload = {
  conversationHistory: { role: 'ai' | 'user'; text: string }[];
  orgContext: OrganizationProfileData | null;
  documentTitle: string;
  policyName: string;
  memoryContent: string;
  relevantContext: string[];
  documentTags: string[];
};

/**
 * Stream the AI's next Q&A question via SSE.
 * Calls onEvent for each token; resolves when the stream ends.
 * The `done` event includes `isComplete: true` when AI appended [[CONTEXT_COMPLETE]].
 */
export const streamQATurn = async (
  payload: QATurnPayload,
  onEvent: (event: QAStreamEvent & { isComplete?: boolean }) => void,
  orgId?: string,
  signal?: AbortSignal,
): Promise<void> => {
  const headers = await getAuthHeaders(orgId);
  const response = await fetch(`${API_BASE_URL}/api/revisr/org-memory/qa-stream`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
    signal,
  });

  if (!response.ok || !response.body) {
    throw new Error('Failed to start Q&A stream');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() ?? '';

    for (const part of parts) {
      if (part.startsWith('data: ')) {
        try {
          const event = JSON.parse(part.slice(6)) as QAStreamEvent & { isComplete?: boolean };
          onEvent(event);
        } catch {
          // malformed chunk — ignore
        }
      }
    }
  }
};
